import React from "react";
import '../css/GettingStarted.css';

const MachineLearning = () => {

  // ==============================
  // Supervised Learning Algorithms
  // ==============================
  const supervisedData = [
    { algorithm: "Linear Regression", type: "Reg", dataSize: "S–M", scaling: "✔", interpretability: "H", overfit: "High", std: "High", pros: "Simple, fast", cons: "Fails with low variance" },
    { algorithm: "Ridge Regression", type: "Reg", dataSize: "S–M", scaling: "✔", interpretability: "H", overfit: "Medium", std: "High", pros: "Handles collinearity", cons: "No feature selection" },
    { algorithm: "Lasso Regression", type: "Reg", dataSize: "S–M", scaling: "✔", interpretability: "H", overfit: "Medium", std: "High", pros: "Feature selection", cons: "Unstable w/ correlated vars" },
    { algorithm: "Elastic Net", type: "Reg", dataSize: "S–M", scaling: "✔", interpretability: "H", overfit: "Medium", std: "High", pros: "Balanced regularization", cons: "Extra tuning" },
    { algorithm: "Logistic Regression", type: "Clf", dataSize: "S–M", scaling: "✔", interpretability: "H", overfit: "Medium", std: "High", pros: "Probabilistic output", cons: "Linear boundary" },
    { algorithm: "Decision Tree", type: "Both", dataSize: "S–M", scaling: "✖", interpretability: "H", overfit: "High", std: "Low", pros: "Interpretable, nonlinear", cons: "Easily overfits" },
    { algorithm: "Random Forest", type: "Both", dataSize: "M–L", scaling: "✖", interpretability: "M", overfit: "Low", std: "Low", pros: "Robust, strong baseline", cons: "Less interpretable" },
    { algorithm: "Extra Trees", type: "Both", dataSize: "M–L", scaling: "✖", interpretability: "M", overfit: "Low", std: "Low", pros: "Fast, low variance", cons: "Higher bias" },
    { algorithm: "Gradient Boosting", type: "Both", dataSize: "M", scaling: "✖", interpretability: "L", overfit: "Medium", std: "Low", pros: "High accuracy", cons: "Sensitive tuning" },
    { algorithm: "XGBoost", type: "Both", dataSize: "M–L", scaling: "✖", interpretability: "L", overfit: "Medium", std: "Low", pros: "Industry standard", cons: "Complex" },
    { algorithm: "LightGBM", type: "Both", dataSize: "L", scaling: "✖", interpretability: "L", overfit: "Medium", std: "Low", pros: "Very fast", cons: "Leaf overfitting" },
    { algorithm: "CatBoost", type: "Both", dataSize: "M", scaling: "✖", interpretability: "L", overfit: "Medium", std: "Low", pros: "Categorical handling", cons: "Slower training" },
    { algorithm: "k-NN", type: "Both", dataSize: "S", scaling: "✔", interpretability: "L", overfit: "High", std: "High", pros: "Simple", cons: "Poor with low variance" },
    { algorithm: "SVM (Linear)", type: "Both", dataSize: "S–M", scaling: "✔", interpretability: "M", overfit: "Low", std: "High", pros: "Works in high-dim", cons: "Hard tuning" },
    { algorithm: "SVM (RBF)", type: "Both", dataSize: "S", scaling: "✔", interpretability: "L", overfit: "Low", std: "High", pros: "Powerful kernels", cons: "Very slow" },
    { algorithm: "Naive Bayes", type: "Clf", dataSize: "S–L", scaling: "✖", interpretability: "M", overfit: "Low", std: "High", pros: "Very fast", cons: "Strong assumptions" },
    { algorithm: "QDA", type: "Both", dataSize: "S", scaling: "✔", interpretability: "M", overfit: "High", std: "High", pros: "Flexible boundaries", cons: "Needs more data" },
  ];

  // ==================
  // Deep Learning Data
  // ==================
  const deepLearningData = [
    { algorithm: "MLP", dataType: "Tabular", dataSize: "M", scaling: "✔", interpretability: "L", overfit: "High", std: "High", pros: "Universal approximator", cons: "Needs tuning" },
    { algorithm: "CNN", dataType: "Images", dataSize: "L", scaling: "✔", interpretability: "L", overfit: "Medium", std: "High", pros: "Auto features", cons: "Data hungry" },
    { algorithm: "RNN", dataType: "Sequence", dataSize: "M", scaling: "✔", interpretability: "L", overfit: "Medium", std: "High", pros: "Temporal modeling", cons: "Vanishing gradients" },
    { algorithm: "LSTM", dataType: "Sequence", dataSize: "M–L", scaling: "✔", interpretability: "L", overfit: "Medium", std: "High", pros: "Long dependencies", cons: "Slow" },
    { algorithm: "GRU", dataType: "Sequence", dataSize: "M–L", scaling: "✔", interpretability: "L", overfit: "Medium", std: "High", pros: "Faster than LSTM", cons: "Less expressive" },
    { algorithm: "Transformer", dataType: "Text/Multi", dataSize: "L", scaling: "✔", interpretability: "L", overfit: "Medium", std: "High", pros: "SOTA performance", cons: "Very expensive" },
  ];

  // ===== Styling ======
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "20px"
  };

  const thtd = {
    border: "1px solid #888",
    padding: "6px",
    textAlign: "center",
    fontSize: "14px"
  };

  return (
    <div>

      <h2>Supervised Learning Algorithms</h2>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thtd}>Algorithm</th>
            <th style={thtd}>Type</th>
            <th style={thtd}>Data Size</th>
            <th style={thtd}>Scaling</th>
            <th style={thtd}>Interpretability</th>
            <th style={thtd}>Overfitting Risk</th>
            <th style={thtd}>STD Deviation</th>
            <th style={thtd}>Pros</th>
            <th style={thtd}>Cons</th>
          </tr>
        </thead>

        <tbody>
          {supervisedData.map((row, i) => (
            <tr key={i}>
              <td style={thtd}>{row.algorithm}</td>
              <td style={thtd}>{row.type}</td>
              <td style={thtd}>{row.dataSize}</td>
              <td style={thtd}>{row.scaling}</td>
              <td style={thtd}>{row.interpretability}</td>
              <td style={thtd}>{row.overfit}</td>
              <td style={thtd}>{row.std}</td>
              <td style={thtd}>{row.pros}</td>
              <td style={thtd}>{row.cons}</td>
            </tr>
          ))}
        </tbody>
      </table>


      <h2 style={{ marginTop: "40px" }}>Deep Learning Algorithms</h2>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thtd}>Algorithm</th>
            <th style={thtd}>Data Type</th>
            <th style={thtd}>Data Size</th>
            <th style={thtd}>Scaling</th>
            <th style={thtd}>Interpretability</th>
            <th style={thtd}>Overfitting Risk</th>
            <th style={thtd}>STD Deviation</th>
            <th style={thtd}>Pros</th>
            <th style={thtd}>Cons</th>
          </tr>
        </thead>

        <tbody>
          {deepLearningData.map((row, i) => (
            <tr key={i}>
              <td style={thtd}>{row.algorithm}</td>
              <td style={thtd}>{row.dataType}</td>
              <td style={thtd}>{row.dataSize}</td>
              <td style={thtd}>{row.scaling}</td>
              <td style={thtd}>{row.interpretability}</td>
              <td style={thtd}>{row.overfit}</td>
              <td style={thtd}>{row.std}</td>
              <td style={thtd}>{row.pros}</td>
              <td style={thtd}>{row.cons}</td>
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );
};

export default MachineLearning;
